import { useMemo } from "react";
import type { Topic } from "~/lqs";
import type { AsyncOperationSnapshot } from "~/types";
import { useSkipToFirstTimestamp, useUpdatePanelBuffering } from "../../hooks";
import type {
  PairedTopic,
  TerminalPanel,
  TerminalTopicDescriptor,
} from "../../panels";
import { usePairedTopics } from "../../panels";
import { useLoadedPlaybackSource } from "../../playback";
import type {
  ExactRecordsRequest,
  LimitedRecordsRequest,
  PlayerRecord,
  RecordStore,
  StoreSnapshot,
} from "../../record-store";
import {
  mergeSubscriptions,
  NoRecordsError,
  useStoreSnapshot,
} from "../../record-store";

interface StoreRequest {
  pairedTopic: PairedTopic<TerminalTopicDescriptor>;
  count: number;
  timestamp: bigint;
}

export interface TerminalData {
  topicId: Topic["id"];
  count: number;
  timestamp: bigint;
  records: Array<PlayerRecord<"default">>;
  selectedRecord: PlayerRecord<"default"> | null;
  field: string | null;
  mostRecentRecordIndex: number;
  firstTopicRecordTimestamp: bigint | null;
  lastTopicRecordTimestamp: bigint | null;
}

function createRecordsRequests(
  pairedTopic: PairedTopic<TerminalTopicDescriptor>,
  count: number,
  timestamp: bigint,
): {
  primaryRequest: LimitedRecordsRequest<"default">;
  firstRecordRequest: LimitedRecordsRequest<"default"> | null;
  lastRecordRequest: LimitedRecordsRequest<"default"> | null;
  selectedRecordRequest: ExactRecordsRequest<"default"> | null;
} {
  const { descriptor, topic } = pairedTopic;

  return {
    primaryRequest: {
      type: "limited",
      recordType: "default",
      topicId: topic.id,
      frequency: null,
      count,
      timestamp,
    },
    // These next two requests are a bit hacky. The visualization needs to know
    // if its records include the first and last records of the topic. Ideally
    // we could just compare directly against the topic's start and end times.
    // However, because the store internally can adjust the topic's bounds if
    // the original first or last topic appears to have been deleted, we could
    // end up having the true first and/or last records but not being able to
    // know since we can't directly access the updated topic bounds from the
    // store.
    //
    // What can be done without needing to make store changes is to request a
    // single record from both the start and end of the topic. If the topic
    // bounds we can access are still accurate, the associated records should
    // be returned. If those records were deleted, the store will grab the next
    // record greater than the original start time (for the first record
    // request) and the next record less than the original start time (for the
    // last record request), both of which should be the new first and last
    // records, respectively. Then, we can just compare those records'
    // timestamps to the actual set of records from the primary request.
    firstRecordRequest:
      topic.startTime != null
        ? {
            type: "limited",
            recordType: "default",
            topicId: topic.id,
            frequency: null,
            count: 1,
            timestamp: topic.startTime,
          }
        : null,
    lastRecordRequest:
      topic.endTime != null
        ? {
            type: "limited",
            recordType: "default",
            topicId: topic.id,
            frequency: null,
            count: 1,
            timestamp: topic.endTime,
          }
        : null,
    selectedRecordRequest:
      descriptor.selectedRecordTimestamp != null
        ? {
            type: "exact",
            recordType: "default",
            topicId: topic.id,
            timestamps: [descriptor.selectedRecordTimestamp],
          }
        : null,
  };
}

function subscribeToStore(
  pairedTopic: PairedTopic<TerminalTopicDescriptor>,
  count: number,
  timestamp: bigint,
  limit: number,
  prefetchBehind: number,
  prefetchAhead: number,
  store: RecordStore,
  notify: () => void,
): () => void {
  const {
    primaryRequest,
    firstRecordRequest,
    lastRecordRequest,
    selectedRecordRequest,
  } = createRecordsRequests(pairedTopic, count, timestamp);

  const { topic } = pairedTopic;

  return mergeSubscriptions([
    store.subscribe({
      ...primaryRequest,
      limit,
      topicStartTime: topic.startTime,
      topicEndTime: topic.endTime,
      prefetchBehind,
      prefetchAhead,
      notify,
    }),
    firstRecordRequest != null &&
      store.subscribe({
        ...firstRecordRequest,
        limit: 1,
        topicStartTime: topic.startTime,
        topicEndTime: topic.endTime,
        prefetchBehind: 0,
        prefetchAhead: 0,
        notify,
      }),
    lastRecordRequest != null &&
      store.subscribe({
        ...lastRecordRequest,
        limit: 1,
        topicStartTime: topic.startTime,
        topicEndTime: topic.endTime,
        prefetchBehind: 0,
        prefetchAhead: 0,
        notify,
      }),
    selectedRecordRequest != null &&
      store.subscribe({
        ...selectedRecordRequest,
        notify,
      }),
  ]);
}

function getSnapshot(
  pairedTopic: PairedTopic<TerminalTopicDescriptor>,
  count: number,
  timestamp: bigint,
  store: RecordStore,
): AsyncOperationSnapshot<TerminalData> {
  const {
    primaryRequest,
    firstRecordRequest,
    lastRecordRequest,
    selectedRecordRequest,
  } = createRecordsRequests(pairedTopic, count, timestamp);

  const primaryResponse = store.getRecords(primaryRequest);
  const firstRecordResponse =
    firstRecordRequest != null ? store.getRecords(firstRecordRequest) : null;
  const lastRecordResponse =
    lastRecordRequest != null ? store.getRecords(lastRecordRequest) : null;
  const selectedRecordResponse =
    selectedRecordRequest != null
      ? store.getRecords(selectedRecordRequest)
      : null;

  const { descriptor, topic } = pairedTopic;

  if (primaryResponse.status === "rejected") {
    return primaryResponse;
  }

  if (firstRecordResponse?.status === "rejected") {
    return firstRecordResponse;
  }

  if (lastRecordResponse?.status === "rejected") {
    return lastRecordResponse;
  }

  if (selectedRecordResponse?.status === "rejected") {
    return selectedRecordResponse;
  }

  if (primaryResponse.status === "pending") {
    return primaryResponse;
  }

  if (firstRecordResponse?.status === "pending") {
    return firstRecordResponse;
  }

  if (lastRecordResponse?.status === "pending") {
    return lastRecordResponse;
  }

  if (selectedRecordResponse?.status === "pending") {
    return selectedRecordResponse;
  }

  const { value: records } = primaryResponse;
  const firstRecord = firstRecordResponse?.value.at(0);
  const lastRecord = lastRecordResponse?.value.at(0);

  if (records.length === 0) {
    return {
      status: "rejected",
      reason: new NoRecordsError(),
    };
  }

  let selectedRecord = null;
  if (
    descriptor.selectedRecordTimestamp != null &&
    selectedRecordResponse != null
  ) {
    selectedRecord = selectedRecordResponse.value.at(0) ?? null;
  }

  const mostRecentRecordIndex = records.findLastIndex(
    (record) => record.timestamp <= timestamp,
  );

  return {
    status: "fulfilled",
    value: {
      topicId: topic.id,
      count,
      timestamp,
      records,
      selectedRecord,
      field: descriptor.field,
      mostRecentRecordIndex,
      firstTopicRecordTimestamp: firstRecord?.timestamp ?? null,
      lastTopicRecordTimestamp: lastRecord?.timestamp ?? null,
    },
  };
}

const count = 500;
const limit = 100;
const prefetchBehind = 100;
const prefetchAhead = 200;

export function useTerminalRecords({
  panel,
}: {
  panel: TerminalPanel;
}): [
  terminalRecordsSnapshot: StoreSnapshot<StoreRequest, TerminalData>,
  isPlaceholderSnapshot: boolean,
] {
  const playbackSource = useLoadedPlaybackSource();

  const [pairedTopic] = usePairedTopics(panel);

  const storeSnapshotResult = useStoreSnapshot(
    useMemo(() => {
      const request: StoreRequest = {
        pairedTopic,
        count,
        timestamp: playbackSource.timestamp,
      };

      return {
        request,
        subscribe: subscribeToStore.bind(
          null,
          pairedTopic,
          count,
          playbackSource.timestamp,
          limit,
          prefetchBehind,
          prefetchAhead,
        ),
        getSnapshot: getSnapshot.bind(
          null,
          pairedTopic,
          count,
          playbackSource.timestamp,
        ),
      };
    }, [pairedTopic, playbackSource.timestamp]),
  );

  useSkipToFirstTimestamp(
    panel,
    pairedTopic.topic.startTime,
    storeSnapshotResult.snapshot.status === "fulfilled",
  );

  useUpdatePanelBuffering(
    storeSnapshotResult.snapshot.status === "pending" ||
      storeSnapshotResult.isPlaceholder,
  );

  return [storeSnapshotResult.snapshot, storeSnapshotResult.isPlaceholder];
}
